import {ReactComponent as FlipIcon} from "../../core/ui/icon/Flip.svg";

import "./_swap-form.scss";

import {useState} from "react";

import Form from "../../component/form/Form";
import {useSwapFormState, useSwapFormDispatch} from "./context/SwapFormContext";
import {
  useSelectedAccount,
  useSelectedAccountAssets
} from "../../wallet/util/context/WalletContext.utils";
import {
  generateAlgoAssetListItemData,
  mapAssetToAssetListItemData
} from "../../asset/util/assetUtils";
import {
  determineSwapAmounts,
  getSwapFormErrorMessage,
  getSwapFundSufficiencyInformation
} from "./SwapForm.utils";
import AssetFormElement from "../../asset/form-element/AssetFormElement";
import {SwapFormTextFieldNames} from "./SwapForm.types";
import SeperatorWithAction from "../../component/seperator-with-action/SeperatorWithAction";
import SwapFormInfoMessage from "./info/message/SwapFormInfoMessage";
import ConnectWalletButton from "../../wallet/connect/button/ConnectWalletButton";
import SwapPriceDetails from "./price-details/SwapPriceDetails";
import {useFetchAndSaveQuote} from "./util/hook/swapQuoteHooks";
import SwapButton from "../../component/swap-button/SwapButton";
import Typography from "../../component/typography/Typography";
import SelectAssetModal from "./select-asset-modal/SelectAssetModal";
import {AssetListItemData} from "../../asset/util/assetModels";

function SwapForm() {
  const state = useSwapFormState();
  const dispatch = useSwapFormDispatch();
  const selectedAccount = useSelectedAccount();
  const selectedAccountAssets = useSelectedAccountAssets();
  const assetDataMapper = mapAssetToAssetListItemData(
    selectedAccount,
    selectedAccountAssets
  );
  const fundSufficiencyInfo = getSwapFundSufficiencyInformation({
    account: selectedAccount,
    accountAssets: selectedAccountAssets,
    swapFormState: state
  });

  const {assetInAmount, assetOutAmount} = determineSwapAmounts(state);

  const hasConnectedWallet = Boolean(selectedAccount);
  const hasEnteredAmount = Boolean(assetInAmount) || Boolean(assetOutAmount);
  const assetIn = state.assetIn.asset && assetDataMapper(state.assetIn.asset);
  const assetOut = state.assetOut.asset && assetDataMapper(state.assetOut.asset);

  const fetchAndSaveQuoteAsyncState = useFetchAndSaveQuote(state.assetIn, state.assetOut);
  const errorMessage = getSwapFormErrorMessage(
    fundSufficiencyInfo,
    fetchAndSaveQuoteAsyncState.error
  );

  const [selectAssetModalView, setsSelectAssetModalView] = useState<
    null | "select-asset-in" | "select-asset-out"
  >(null);

  return (
    <Form customClassName={"is-flex-column swap-form"}>
      <AssetFormElement
        inputProps={{
          id: SwapFormTextFieldNames.AssetInAmount,
          name: SwapFormTextFieldNames.AssetInAmount,
          onChange: handleAmountChange,
          value: assetInAmount,
          "aria-describedby": "swap-form-error"
        }}
        typography={"body"}
        asset={assetIn || generateAlgoAssetListItemData(selectedAccount)}
        onBrowseToSelectAssetView={handleBrowseToSelectAssetView("asset-in")}
        hasError={
          Boolean(selectedAccount) && !fundSufficiencyInfo.assetInBalance.hasEnough
        }
      />

      <SeperatorWithAction
        customClassName={"swap-form__flip-assets"}
        icon={<FlipIcon />}
        buttonProps={{onClick: handleFlipAssetOrder, "aria-label": "Flip asset order"}}
      />

      <AssetFormElement
        customClassName={"swap-form__asset-out"}
        inputProps={{
          id: SwapFormTextFieldNames.AssetOutAmount,
          name: SwapFormTextFieldNames.AssetOutAmount,
          onChange: handleAmountChange,
          value: assetOutAmount,
          "aria-describedby": "swap-form-error"
        }}
        onBrowseToSelectAssetView={handleBrowseToSelectAssetView("asset-out")}
        typography={"body"}
        asset={assetOut}
      />

      {!hasConnectedWallet && (
        <>
          <SwapFormInfoMessage
            customClassName={"swap-form__info-message"}
            message={"You should connect your wallet to swap"}
          />

          <ConnectWalletButton />
        </>
      )}

      {selectAssetModalView && (
        <SelectAssetModal
          isOpen={true}
          onClose={handleCloseSelectAssetView}
          onSelect={handleSelectAsset}
          assetsToFilterOut={getAssetsToFilterOut()}
        />
      )}

      {!hasEnteredAmount && hasConnectedWallet && (
        <SwapFormInfoMessage
          customClassName={"swap-form__info-message"}
          message={"Please enter an amount"}
        />
      )}

      {hasConnectedWallet && hasEnteredAmount && (
        <SwapPriceDetails asyncProcessState={fetchAndSaveQuoteAsyncState} />
      )}

      {/* This renders invisible error message text for a11y users */}
      {errorMessage && (
        <Typography
          id={"swap-form-error"}
          as={"span"}
          customClassName={"screen-readers-only"}>
          {errorMessage}
        </Typography>
      )}

      {hasConnectedWallet && (
        <SwapButton asyncProcessState={fetchAndSaveQuoteAsyncState} />
      )}
    </Form>
  );

  function handleAmountChange(name: string, value: string) {
    dispatch({
      type: "SET_AMOUNT",
      name: name as SwapFormTextFieldNames,
      value
    });
  }

  function handleSelectAsset(asset: AssetListItemData) {
    dispatch({
      type: "SET_SWAP_FORM_ASSETS",
      assetIn: selectAssetModalView === "select-asset-in" ? asset : state.assetIn.asset,
      assetOut: selectAssetModalView === "select-asset-out" ? asset : state.assetOut.asset
    });

    handleCloseSelectAssetView();
  }

  function handleCloseSelectAssetView() {
    setsSelectAssetModalView(null);
  }

  function handleBrowseToSelectAssetView(forWhichPair: "asset-in" | "asset-out") {
    return () => {
      setsSelectAssetModalView(
        forWhichPair === "asset-in" ? "select-asset-in" : "select-asset-out"
      );
    };
  }

  function getAssetsToFilterOut() {
    if (selectAssetModalView === "select-asset-in" && assetOut?.id) {
      return [assetOut.id];
    } else if (selectAssetModalView === "select-asset-out" && assetIn?.id) {
      return [assetIn.id];
    }

    return undefined;
  }

  function handleFlipAssetOrder() {
    dispatch({
      type: "FLIP_ASSET_ORDER"
    });
  }
}

export default SwapForm;
